import { ITicket, ITicketFilter } from '@/types/ticket';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { userSpecificStorage } from './userSpecificStorage';
import { DEFAULT_TICKET_LIST_TAB_ACTIVE } from '../consts';
import { IPaginatorParams } from '@/types/genericDataTable';
import { IActivity } from '@/types/activities';
import { IActivityFrontContentResponse } from '@/types/activities/content';

export type ConfigActivityType = 'note' | 'send_template' | 'send_email' | 'email_response' | 'send_message' | 'private_message';

export interface ConfigActivity {
    id: string;
    ticketId: string;
    type: ConfigActivityType;
    hideable?: boolean;
}

export interface TicketStore {
    _isHydrated: boolean; // 1. Determina si la store ha sido rehidratada con los datos del LS
    setIsHydrated: (loaded: boolean) => void;

    // loadingInitialData: boolean;
    // setLoadingInitialData: (loading: boolean) => void;

    // Tickets abiertos por ID
    // openedTicketsMap: Map<string, ITicket>;
    // setOpenedTickets: (tickets: ITicket[]) => void;
    // Orden de las pestañas
    openedTicketsOrder: string[];
    addOpenedTicket: (id: string) => void;
    removeOpenedTicket: (id: string) => void;
    // updateOpenedTicket: (id: string, partialTicket: Partial<ITicket>) => void;
    // replaceOpenedTicket: (id: string, ticket: ITicket) => void;
    resetOpenedTickets: () => void;

    // Último ticket activo
    lastTicketActive?: string;
    setLastTicketActive: (id?: string) => void;

    //Listado de tickets
    paginator: Record<
        TicketStore['tabActive'],
        IPaginatorParams<ITicket> | undefined
    >;
    setPaginator: (paginator: TicketStore['paginator']) => void;

    //Establece el paginador por pestaña para guardar en LS
    setPaginatorByTab: (
        tab: TicketStore['tabActive'],
        paginatorParams?: IPaginatorParams<ITicket>,
    ) => void;

    filters: Record<TicketStore['tabActive'], ITicketFilter>;
    //Reemplaza el objeto filter completo
    setAllFilters: (filter: TicketStore['filters']) => void;
    //Actualiza solo las propiedades indicadas del objeto filter
    updateFilter: (
        tab: TicketStore['tabActive'],
        filter: Partial<ITicketFilter>,
    ) => void;
    //Elimina las propiedades indicadas del objeto filter
    removeFilter: (
        tab: TicketStore['tabActive'],
        keys: (keyof ITicketFilter)[],
    ) => void;

    //Reemplaza un filtro
    replaceFilter: (
        tab: TicketStore['tabActive'],
        filter: Partial<ITicketFilter>,
    ) => void;

    clearAllTabFilters: () => void;

    tabActive: 'my_tickets' | 'my_groups' | 'my_history';
    setTabActive: (tab: TicketStore['tabActive']) => void;

    customFilter: string;
    customFilterName: string;
    setCustomFilter: (customFilter: string) => void;
    setCustomFilterName: (customFilterName: string) => void;

    ticketActivitiesVisibility: Record<string, boolean>;
    setTicketActivitiesVisibility: (ticketId: string, visibility: boolean) => void;

    activitySearchTerm: string;
    setActivitySearchTerm: (term: string) => void;

    // Draft message state for response activities
    responseActivities: Record<string, IActivity<IActivityFrontContentResponse>>;
    setResponseActivity: (
        ticketId: string,
        activity: IActivity<IActivityFrontContentResponse> | null,
    ) => void;
    clearResponseActivity: (ticketId: string) => void;
    updateResponseActivity: (
        ticketId: string,
        updater: (
            prev: IActivity<IActivityFrontContentResponse>,
        ) => IActivity<IActivityFrontContentResponse>,
    ) => void;

    // Reply state - tracks which activity is being replied to
    replyingToActivity: Record<string, string | undefined>; // ticketId -> activityId
    setReplyingToActivity: (ticketId: string, activityId?: string) => void;

    // ConfigActivities state - temporary UI states for forms (note, etc.)
    configActivities: Record<string, ConfigActivity[]>; // ticketId -> ConfigActivity[]
    addConfigActivity: (ticketId: string, config: Omit<ConfigActivity, 'id'>) => string; // Returns the generated id
    removeConfigActivityById: (ticketId: string, id: string) => void;
    removeConfigActivityByType: (ticketId: string, type: ConfigActivityType) => void;
}

const useTicketStore = create<TicketStore>()(
    persist(
        (set) => ({
            //*START Estados del detalle del ticket

            _isHydrated: false,
            setIsHydrated: (_isHydrated) => set({ _isHydrated }),

            openedTicketsOrder: [],
            addOpenedTicket: (ticketId) =>
                set((state) => {
                    if (state.openedTicketsOrder.includes(ticketId))
                        return state;

                    return {
                        openedTicketsOrder: [
                            ...state.openedTicketsOrder,
                            ticketId,
                        ],
                    };
                }),
            removeOpenedTicket: (id) =>
                set((state) => {
                    return {
                        openedTicketsOrder: state.openedTicketsOrder.filter(
                            (tid) => tid !== id,
                        ),
                    };
                }),
            resetOpenedTickets: () => set({ openedTicketsOrder: [] }),

            lastTicketActive: undefined,
            setLastTicketActive: (lastTicketActive) =>
                set({ lastTicketActive }),
            //*END Estados del detalle del ticket

            //*START Estados del listado de tickets
            setAllFilters: (filters) => set({ filters }),
            filters: {
                my_groups: {},
                my_history: {},
                my_tickets: {},
            },

            updateFilter: (tab, partialFilter) =>
                set((state) => ({
                    filters: {
                        ...(state.filters ?? {}),
                        [tab]: { ...state.filters[tab], ...partialFilter },
                    },
                })),

            replaceFilter: (tab, partialFilter) =>
                set((state) => ({
                    filters: {
                        ...(state.filters ?? {}),
                        [tab]: partialFilter ?? {},
                    },
                })),

            clearAllTabFilters: () =>
                set(() => ({
                    filters: {
                        my_groups: {},
                        my_history: {},
                        my_tickets: {},
                    },
                })),

            removeFilter: (tab, keys) =>
                set((state) => {
                    if (!state.filters[tab]) return state;
                    const newFilter = { ...state.filters[tab] };

                    keys.forEach((key) => {
                        delete newFilter[key];
                    });

                    return { filters: { ...state.filters, [tab]: newFilter } };
                }),

            paginator: {
                my_groups: {},
                my_history: {},
                my_tickets: {},
            },

            setPaginator: (paginator) => set({ paginator }),

            setPaginatorByTab: (tab, paginatorParams) =>
                set((state) => ({
                    paginator: {
                        ...(state.paginator ?? {}),
                        [tab]: paginatorParams,
                    },
                })),

            tabActive: DEFAULT_TICKET_LIST_TAB_ACTIVE,
            setTabActive: (tabActive) => set({ tabActive }),

            customFilter: '',
            customFilterName: '',
            setCustomFilter: (customFilter: string) => set({ customFilter }),
            setCustomFilterName: (customFilterName: string) => set({ customFilterName }),
            //*END Estados del listado de tickets

            ticketActivitiesVisibility: {},
            setTicketActivitiesVisibility: (ticketId, visibility) => set((state) => ({ticketActivitiesVisibility: {...state.ticketActivitiesVisibility, [ticketId]: visibility}})),

            activitySearchTerm: '',
            setActivitySearchTerm: (term) => set({ activitySearchTerm: term }),

            // Draft message state
            responseActivities: {},
            setResponseActivity: (ticketId, activity) =>
                set((state) => {
                    if (!activity) {
                        const { [ticketId]: _, ...rest } = state.responseActivities;

                        return { responseActivities: rest };
                    }

                    return {
                        responseActivities: {
                            ...state.responseActivities,
                            [ticketId]: activity,
                        },
                    };
                }),
            clearResponseActivity: (ticketId) =>
                set((state) => {
                    const { [ticketId]: _, ...rest } = state.responseActivities;

                    return { responseActivities: rest };
                }),
            updateResponseActivity: (ticketId, updater) =>
                set((state) => {
                    const current = state.responseActivities[ticketId];
                    if (!current) return state;

                    return {
                        responseActivities: {
                            ...state.responseActivities,
                            [ticketId]: updater(current),
                        },
                    };
                }),

            // Reply state
            replyingToActivity: {},
            setReplyingToActivity: (ticketId, activityId) =>
                set((state) => ({
                    replyingToActivity: {
                        ...state.replyingToActivity,
                        [ticketId]: activityId,
                    },
                })),

            // ConfigActivities state
            configActivities: {},
            addConfigActivity: (ticketId, config) => {
                const id = crypto.randomUUID();
                set((state) => {
                    const existing = state.configActivities[ticketId] ?? [];
                    // Remove existing configActivity of the same type
                    const filtered = existing.filter((c) => c.type !== config.type);

                    return {
                        configActivities: {
                            ...state.configActivities,
                            [ticketId]: [
                                ...filtered,
                                { ...config, id, ticketId },
                            ],
                        },
                    };
                });

                return id;
            },
            removeConfigActivityById: (ticketId, id) =>
                set((state) => {
                    const existing = state.configActivities[ticketId] ?? [];

                    return {
                        configActivities: {
                            ...state.configActivities,
                            [ticketId]: existing.filter((c) => c.id !== id),
                        },
                    };
                }),
            removeConfigActivityByType: (ticketId, type) =>
                set((state) => {
                    const existing = state.configActivities[ticketId] ?? [];

                    return {
                        configActivities: {
                            ...state.configActivities,
                            [ticketId]: existing.filter((c) => c.type !== type),
                        },
                    };
                }),
        }),
        {
            name: 'ticket_store',
            storage: createJSONStorage(() => userSpecificStorage),
            partialize: (state) => {
                return {
                    openedTicketsOrder: state.openedTicketsOrder,
                    lastTicketActive: state.lastTicketActive,
                    tabActive: state.tabActive,
                    paginator: state.paginator,
                    filters: state.filters,
                    ticketActivitiesVisibility: state.ticketActivitiesVisibility,
                    responseActivities: state.responseActivities,
                    replyingToActivity: state.replyingToActivity,
                };
            },
            merge: (persistedState, currentState) => {
                const pState = persistedState as Partial<TicketStore>;

                return {
                    ...currentState,
                    ...pState,
                };
            },
        },
    ),
);

export default useTicketStore;

const { onFinishHydration } = useTicketStore.persist;

onFinishHydration(() => {
    // Esto se ejecuta cuando los datos del LS han sido cargados al estado

    useTicketStore.setState({ _isHydrated: true });
});
